import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Select } from '@/shared/ui';
import { track, type AuthOrigem } from '@/lib/analytics';
import {
  MEDICAL_SPECIALTIES,
  PASSWORD_MIN_LENGTH,
  PHONE_MASK_LENGTH,
  PRIVACY_POLICY_URL,
  isFullName,
  isValidPhone,
  maskPhone,
  ExistingAccountError,
  type MedicalSpecialty,
  type ProfileDraft,
  type SignUpInput,
} from '../../domain/entities/auth.entity';
import { useLeadCapture } from '@/modules/lead/presentation/hooks/use-lead-capture';
import { useAuth } from '../hooks/use-auth';

interface SignUpFormProps {
  /** De onde a pessoa chegou: vai nos eventos de lead. */
  origem: AuthOrigem;
  onSuccess: () => void;
  /**
   * O e-mail já tem conta: quem hospeda leva ao login, com ele preenchido, e
   * com o que já foi digitado — se a conta não tiver perfil aqui, o cadastro
   * desta plataforma se completa com isso, sem pedir de novo.
   */
  onExistingAccount: (email: string, draft: ProfileDraft) => void;
}

/**
 * O cadastro, com os campos e as validações do `SignUpForm` do
 * `enamed-feedback-lp`: nome completo, e-mail, telefone com máscara, senha com
 * confirmação, especialidade pretendida (a lista do ENAMED) e o aceite da
 * política.
 *
 * Cria a conta **e entra**. E-mail que já tem conta não vira erro na tela:
 * vira o desvio para o login (`onExistingAccount`), como no ENAMED.
 */
export function SignUpForm({
  origem,
  onSuccess,
  onExistingAccount,
}: SignUpFormProps) {
  const auth = useAuth();
  const { captureSignUp } = useLeadCapture();
  const signUp = useMutation({
    mutationFn: (input: SignUpInput) => auth.signUp(input),
    onSuccess: () => {
      // No sucesso, e não no clique: cadastro que falhou não é lead.
      track({ event: 'sign_up', method: 'email', origem });
      track({ event: 'generate_lead', lead_source: origem });
      onSuccess();
    },
    onError: (error, input) => {
      if (error instanceof ExistingAccountError) {
        track({ event: 'cadastro_email_existente', origem });
        onExistingAccount(error.email, {
          fullName: input.fullName,
          phone: input.phone,
          intendedSpecialty: input.intendedSpecialty,
        });
      }
    },
  });

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [intendedSpecialty, setIntendedSpecialty] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  /**
   * Valida no envio, e não a cada tecla — mesmo critério do ENAMED: acusar
   * "as senhas não coincidem" antes de a pessoa terminar de digitar é ruído. O
   * tamanho da senha vem antes da coincidência: duas senhas curtas iguais têm
   * um problema só.
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const nameIncomplete = !isFullName(fullName);
    const phoneIncomplete = !isValidPhone(phone);
    const isTooShort = password.length < PASSWORD_MIN_LENGTH;
    const doesNotMatch = password !== confirmPassword;

    setNameError(nameIncomplete ? 'Informe seu nome e sobrenome.' : null);
    setPhoneError(
      phoneIncomplete ? 'Informe um telefone válido com DDD.' : null,
    );
    setPasswordError(
      isTooShort
        ? `A senha precisa ter no mínimo ${PASSWORD_MIN_LENGTH} caracteres.`
        : null,
    );
    setConfirmError(
      !isTooShort && doesNotMatch ? 'As senhas não coincidem.' : null,
    );
    setTermsError(
      acceptedTerms ? null : 'Você precisa aceitar a política de privacidade.',
    );

    if (
      nameIncomplete ||
      phoneIncomplete ||
      isTooShort ||
      doesNotMatch ||
      !acceptedTerms
    ) {
      return;
    }

    const cleanName = fullName.trim().replace(/\s+/g, ' ');

    // Antes do cadastro e sem `await`, como no ENAMED: o aceite vale mesmo que
    // a criação da conta falhe depois (e-mail já em uso, API fora), e não pode
    // atrasar nem derrubar o envio do formulário.
    captureSignUp({ email: email.trim(), fullName: cleanName, phone });

    signUp.mutate({
      fullName: cleanName,
      email,
      password,
      phone,
      intendedSpecialty: intendedSpecialty as MedicalSpecialty,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="form-stack" noValidate>
      <Input
        id="fullName"
        label="Nome completo"
        required
        maxLength={241}
        autoComplete="name"
        value={fullName}
        onChange={(e) => {
          setFullName(e.target.value);
          setNameError(null);
        }}
        error={nameError ?? undefined}
        placeholder="Seu nome e sobrenome"
      />

      <Input
        id="email"
        label="Seu melhor e-mail"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="seu@email.com"
      />

      <Input
        id="phone"
        label="Telefone com WhatsApp"
        type="tel"
        required
        maxLength={PHONE_MASK_LENGTH}
        autoComplete="tel"
        value={phone}
        onChange={(e) => {
          // Mascara o que já está no campo, e não a tecla: apagar e colar
          // passam pela mesma regra.
          setPhone(maskPhone(e.target.value));
          setPhoneError(null);
        }}
        error={phoneError ?? undefined}
        placeholder="(00) 00000-0000"
      />

      <Input
        id="password"
        label="Senha"
        type="password"
        required
        autoComplete="new-password"
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setPasswordError(null);
        }}
        error={passwordError ?? undefined}
        helperText={`Mínimo de ${PASSWORD_MIN_LENGTH} caracteres.`}
        placeholder="••••••••"
      />

      <Input
        id="confirmPassword"
        label="Confirmar senha"
        type="password"
        required
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setConfirmError(null);
        }}
        error={confirmError ?? undefined}
        placeholder="••••••••"
      />

      <Select
        id="intendedSpecialty"
        label="Especialidade pretendida"
        required
        placeholder="Selecione uma opção"
        value={intendedSpecialty}
        onChange={(e) => setIntendedSpecialty(e.target.value)}
        options={MEDICAL_SPECIALTIES.map((option) => ({
          value: option,
          label: option,
        }))}
      />

      <div>
        <label className="field-check">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              if (e.target.checked) setTermsError(null);
            }}
          />
          <span>
            Li e aceito a{' '}
            {/* Nova aba: sair da página no meio do formulário levaria junto
                tudo o que já foi digitado. */}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {termsError && <p className="field-error">{termsError}</p>}
      </div>

      {signUp.isError && !(signUp.error instanceof ExistingAccountError) && (
        <div className="alert" role="alert">
          Não conseguimos concluir seu cadastro agora. Tente de novo em alguns
          instantes.
        </div>
      )}

      <Button
        type="submit"
        disabled={signUp.isPending || intendedSpecialty === ''}
      >
        {signUp.isPending ? 'Criando sua conta…' : 'Criar conta'}
      </Button>
    </form>
  );
}
